import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SkeletonDashboardHome } from '@/components/ui/Skeleton';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { Event, Purchase } from '@eventflow/shared-types';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface DashboardSummary {
  upcomingEvents: Event[];
  pendingPaymentsCount: number;
  recentPurchases: Purchase[];
  eventMap: Record<string, Event>; // Para mapear event_id -> Event
}

export default function DashboardHomePage() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSummary = async () => {
      setIsLoading(true);
      setError('');
      try {
        // Obtener eventos del organizador
        const eventsData = await api.get<Event[]>('/events');

        // Obtener compras del organizador
        const purchasesData = await api.get<Purchase[]>('/purchases/organizer');

        // Crear mapa de eventos para lookup rápido
        const eventMap: Record<string, Event> = {};
        eventsData.forEach((event) => {
          eventMap[event.id] = event;
        });

        // Filtrar y ordenar eventos próximos
        const now = new Date();
        const upcomingEvents = eventsData
          .filter((e) => new Date(e.event_date) >= now)
          .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime())
          .slice(0, 3);

        // Contar pagos pendientes
        const pendingPaymentsCount = purchasesData.filter(
          (p) => p.payment_status === 'pending'
        ).length;

        // Obtener compras recientes (últimas 5)
        const recentPurchases = purchasesData
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 5);

        setSummary({
          upcomingEvents,
          pendingPaymentsCount,
          recentPurchases,
          eventMap,
        });
      } catch (err) {
        console.error('Error fetching dashboard summary:', err);
        setError('No se pudo cargar el resumen del dashboard.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary();
  }, []);

  const getPaymentStatusBadge = (status: string) => {
    const badges = {
      pending: <span className="badge badge-yellow">Pendiente</span>,
      confirmed: <span className="badge badge-green">Confirmado</span>,
      failed: <span className="badge badge-red">Fallido</span>,
      cancelled: <span className="badge badge-gray">Cancelado</span>,
    };
    return (
      badges[status as keyof typeof badges] || <span className="badge badge-gray">{status}</span>
    );
  };

  if (isLoading) {
    return <SkeletonDashboardHome />;
  }

  if (error) {
    return (
      <Card className="bg-red-50 border-red-200">
        <p className="text-red-700">{error}</p>
      </Card>
    );
  }

  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Dashboard</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Card Pagos Pendientes */}
        <Card className="bg-yellow-50 border-yellow-200">
          <h2 className="text-lg font-semibold text-yellow-800 mb-4">Pagos Pendientes</h2>
          <p className="text-5xl font-bold text-yellow-700 mb-4">
            {summary?.pendingPaymentsCount ?? 0}
          </p>
          <Link to="/dashboard/payments">
            <Button
              variant="secondary"
              size="sm"
              className="border-yellow-400 text-yellow-800 hover:bg-yellow-100"
            >
              Gestionar Pagos
            </Button>
          </Link>
        </Card>

        {/* Card Próximos Eventos */}
        <Card className="sm:col-span-2 lg:col-span-2" title="Próximos Eventos">
          {summary?.upcomingEvents && summary.upcomingEvents.length > 0 ? (
            <ul className="space-y-3">
              {summary.upcomingEvents.map((event) => (
                <li
                  key={event.id}
                  className="flex justify-between items-center border-b pb-3 last:border-b-0"
                >
                  <div>
                    <Link
                      to={`/dashboard/events/${event.id}/edit`}
                      className="font-semibold text-blue-600 hover:underline"
                    >
                      {event.name}
                    </Link>
                    <p className="text-sm text-gray-500">
                      {formatDate(event.event_date)} - {event.venue_name}
                    </p>
                  </div>
                  <Link to={`/dashboard/events/${event.id}/map`}>
                    <Button variant="secondary" size="sm">
                      Ver Mapa
                    </Button>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No tienes eventos próximos.</p>
          )}
          <div className="mt-6 pt-4 border-t">
            <Link to="/dashboard/events/new">
              <Button variant="primary">➕ Crear Nuevo Evento</Button>
            </Link>
          </div>
        </Card>

        {/* Card Compras Recientes */}
        <Card className="sm:col-span-2 lg:col-span-3" title="Últimas Compras">
          {summary?.recentPurchases && summary.recentPurchases.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Evento
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {summary.recentPurchases.map((purchase) => (
                      <tr key={purchase.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(purchase.created_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {purchase.customer_name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {summary.eventMap[purchase.event_id]?.name || 'Evento no encontrado'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(purchase.total_amount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {getPaymentStatusBadge(purchase.payment_status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 pt-4 border-t">
                <Link to="/dashboard/payments">
                  <Button variant="secondary">Ver Todas las Compras</Button>
                </Link>
              </div>
            </>
          ) : (
            <p className="text-gray-500">No hay compras recientes.</p>
          )}
        </Card>
      </div>
    </div>
  );
}
